import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Edit } from '@mui/icons-material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import StaffAccessRow from './staffAccessRow'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Role } from '@/types/Role'
import styles from './permissionsTable.module.scss'
import { StaffPermissions } from '@/types/StaffRights.ts'

interface PermissionsTableProps {
    permissions: StaffPermissions
    setPermission: (key: keyof StaffPermissions, value: Partial<StaffPermissions[typeof key]>) => void
    allReadChecked: boolean
    allWriteChecked: boolean
    readIndeterminate: boolean
    writeIndeterminate: boolean
    toggleAllRead: () => void
    toggleAllWrite: () => void
    disabled: boolean
    currentDaycare?: Daycare
    userRole?: Role
    currentDaycareRightsAddNewsWithoutValidation: boolean
    showTitleInTable: boolean
}

const PermissionsTable: FunctionComponent<PermissionsTableProps> = ({
    permissions,
    setPermission,
    allReadChecked,
    allWriteChecked,
    readIndeterminate,
    writeIndeterminate,
    toggleAllRead,
    toggleAllWrite,
    disabled,
    currentDaycare,
    userRole,
    currentDaycareRightsAddNewsWithoutValidation,
    showTitleInTable
}: PermissionsTableProps) => {
    const { t } = useTranslation()

    const isRpeOrLaep: boolean = currentDaycare?.type === DaycareType.rpe || currentDaycare?.type === DaycareType.laep

    return (
        <Table size='small'>
            <TableBody>
                <TableRow>
                    {showTitleInTable ? (
                        <TableCell className={`${styles.NoBorder} ${styles.TitleTable}`}>
                            <p className={styles.TitleTable}>{t('staffAccess')}</p>
                        </TableCell>
                    ) : (
                        <TableCell className={styles.NoBorder} />
                    )}
                    <TableCell align='center' className={styles.NoBorder}>
                        <VisibilityIcon />
                    </TableCell>
                    <TableCell align='center' className={styles.NoBorder}>
                        <Edit />
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell className={!showTitleInTable ? styles.NoBorder : ''} />
                    <TableCell align={'center'}>
                        <Checkbox
                            checked={allReadChecked}
                            indeterminate={readIndeterminate}
                            onChange={toggleAllRead}
                            disabled={disabled}
                            className={styles.Checkbox}
                        />
                    </TableCell>
                    <TableCell align={'center'}>
                        <Checkbox
                            checked={allWriteChecked}
                            indeterminate={writeIndeterminate}
                            onChange={toggleAllWrite}
                            disabled={disabled}
                            className={styles.Checkbox}
                        />
                    </TableCell>
                </TableRow>

                <StaffAccessRow
                    title={t('daycareSettings')}
                    readChecked={permissions.daycareSettings.read}
                    setReadChecked={(val) => setPermission('daycareSettings', { read: val })}
                    writeChecked={permissions.daycareSettings.write}
                    setWriteChecked={(val) => setPermission('daycareSettings', { write: val })}
                    disabled={disabled}
                    border={true}
                />

                {currentDaycare?.staffAccess && (
                    <>
                        <TableRow className={styles.BreakLine} />
                        <StaffAccessRow
                            title={t('staffList')}
                            readChecked={permissions.staffList.read}
                            setReadChecked={(val) => setPermission('staffList', { read: val })}
                            writeChecked={permissions.staffList.write}
                            setWriteChecked={(val) => setPermission('staffList', { write: val })}
                            disabled={disabled}
                        />
                        <StaffAccessRow
                            title={t('staffCalendar')}
                            readChecked={permissions.staffCalendar.read}
                            setReadChecked={(val) => setPermission('staffCalendar', { read: val })}
                            writeChecked={permissions.staffCalendar.write}
                            setWriteChecked={(val) => setPermission('staffCalendar', { write: val })}
                            disabled={disabled}
                            border={true}
                        />
                    </>
                )}

                <TableRow className={styles.BreakLine} />
                <StaffAccessRow
                    title={t(isRpeOrLaep ? 'publicList' : 'parentList')}
                    readChecked={permissions.parentList.read}
                    setReadChecked={(val) => setPermission('parentList', { read: val })}
                    writeChecked={permissions.parentList.write}
                    setWriteChecked={(val) => setPermission('parentList', { write: val })}
                    disabled={disabled}
                    border={true}
                />
                <StaffAccessRow
                    title={t(isRpeOrLaep ? 'animationSlots' : 'occasionalSlots')}
                    readChecked={permissions.occasionalSlots.read}
                    setReadChecked={(val) => setPermission('occasionalSlots', { read: val })}
                    writeChecked={permissions.occasionalSlots.write}
                    setWriteChecked={(val) => setPermission('occasionalSlots', { write: val })}
                    disabled={disabled}
                />
                <StaffAccessRow
                    title={t(isRpeOrLaep ? 'regularsGroups' : 'regularSlots')}
                    readChecked={permissions.regularSlots.read}
                    setReadChecked={(val) => setPermission('regularSlots', { read: val })}
                    writeChecked={permissions.regularSlots.write}
                    setWriteChecked={(val) => setPermission('regularSlots', { write: val })}
                    disabled={disabled}
                    border={true}
                />

                {currentDaycare?.newsAccess && (
                    <>
                        <TableRow className={styles.BreakLine} />
                        <StaffAccessRow
                            title={t('news')}
                            readChecked={permissions.news.read}
                            setReadChecked={(val) => setPermission('news', { read: val })}
                            writeChecked={permissions.news.write}
                            setWriteChecked={(val) => setPermission('news', { write: val })}
                            disabled={disabled}
                        />
                        <StaffAccessRow
                            title={t('addNewsWithoutValidation')}
                            writeChecked={permissions.addNewsWithoutValidation.write}
                            setWriteChecked={(val) => setPermission('addNewsWithoutValidation', { write: val })}
                            disabled={
                                disabled ||
                                (currentDaycare.haveDaycareGroup === true &&
                                    !currentDaycareRightsAddNewsWithoutValidation &&
                                    userRole === Role.staff)
                            }
                            border={true}
                        />
                    </>
                )}

                {currentDaycare?.suggestAccess && (
                    <>
                        <TableRow className={styles.BreakLine} />
                        <StaffAccessRow
                            title={t('suggestions')}
                            readChecked={permissions.suggestions.read}
                            setReadChecked={(val) => setPermission('suggestions', { read: val })}
                            writeChecked={permissions.suggestions.write}
                            setWriteChecked={(val) => setPermission('suggestions', { write: val })}
                            disabled={disabled}
                        />
                    </>
                )}

                {currentDaycare?.taskNoteAccess && (
                    <>
                        <TableRow className={styles.BreakLine} />
                        <StaffAccessRow
                            title={t('tasksAndNotes')}
                            readChecked={permissions.taskNote.read}
                            setReadChecked={(val) => setPermission('taskNote', { read: val })}
                            writeChecked={permissions.taskNote.write}
                            setWriteChecked={(val) => setPermission('taskNote', { write: val })}
                            disabled={disabled}
                        />
                    </>
                )}

                {currentDaycare?.mealAccess && (
                    <>
                        <TableRow className={styles.BreakLine} />
                        <StaffAccessRow
                            title={t('meal')}
                            readChecked={permissions.meal.read}
                            setReadChecked={(val) => setPermission('meal', { read: val })}
                            writeChecked={permissions.meal.write}
                            setWriteChecked={(val) => setPermission('meal', { write: val })}
                            disabled={disabled}
                        />
                    </>
                )}
            </TableBody>
        </Table>
    )
}

export default PermissionsTable
